import React, { ReactElement, useState } from "react";
import axios from "axios";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import useSWR from "swr";
import { createBrowserClient } from "@supabase/ssr";
import { Avatar as RadixAvatar } from "@radix-ui/themes";
import {
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  ClockIcon,
  IdentificationIcon,
  InboxStackIcon,
  TicketIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Account } from "@types";
import {
  COUNTRY_CODE,
  SUPABASE_ANON_KEY,
  SUPABASE_URL,
} from "@utils/constants";
import { authorizedFetcher } from "@utils/fetchers";
import { useSupabaseSession } from "@utils/hooks";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";
import { analytics } from "@components/analytics";
import { Heading, Text, Button } from "@components/theming";
import {
  PopoverRoot,
  PopoverContent,
  PopoverTrigger,
} from "@components/popovers/accountPopover";
import WavingHand from "@components/wavingHand";

interface NavLinkProps {
  setOpen: (open: boolean) => void;
  href?: string;
  onClick?: () => void;
  icon: ReactElement<{ className: string }>;
  label: string;
}

const NavLink = ({ setOpen, href, onClick, icon, label }: NavLinkProps) => {
  const isShopsite = useIsShopsite();

  const renderedIcon = React.cloneElement(icon, {
    className: joinClassNames(
      "stroke-2 h-5 w-5 transition-colors duration-300 ease-in-out",
      isShopsite
        ? "text-indigo-500 group-hover:text-indigo-600"
        : "group-hover:text-[color:var(--accent-10)]"
    ),
  });

  const inner = (
    <>
      <div className="flex gap-2 items-center w-full">
        {isShopsite ? (
          <span className="bg-indigo-50 group-hover:bg-indigo-100 rounded-md p-1 transition-colors duration-300 ease-in-out">
            {renderedIcon}
          </span>
        ) : (
          <RadixAvatar
            size="2"
            fallback={renderedIcon}
            className="[&>span]:transition-colors [&>span]:duration-300 [&>span]:ease-in-out"
          />
        )}
        <Text
          as="span"
          weight="medium"
          sharedClasses="transition-colors duration-300 ease-in-out"
          whitelabelClasses="group-hover:text-[color:var(--accent-11)]"
          shopsiteClasses="group-hover:text-indigo-600 font-medium"
        >
          {label}
        </Text>
      </div>
      <div className="flex items-center justify-center w-0 group-hover:w-7 transition-width ease-in-out duration-150">
        <ArrowRightIcon
          className={joinClassNames(
            "stroke-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity ease-in-out duration-300 delay-100",
            isShopsite ? "text-indigo-600" : "text-[color:var(--accent-11)]"
          )}
        />
      </div>
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        onClick={() => setOpen(false)}
        className={joinClassNames(
          "group flex gap-2 items-center justify-between px-2 py-1.5",
          isShopsite ? "rounded-lg" : ""
        )}
      >
        {inner}
      </Link>
    );
  } else {
    return (
      <button
        onClick={onClick}
        className={joinClassNames(
          "group flex gap-2 items-center justify-between px-2 py-1.5",
          isShopsite ? "rounded-lg" : ""
        )}
      >
        {inner}
      </button>
    );
  }
};

export default function AccountPopover() {
  const supabase = createBrowserClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);
  const t = useTranslations("Account.popover");
  const session = useSupabaseSession();
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const { data: account } = useSWR<Account>(
    session && session.access_token ? ["account", session.access_token] : null,
    authorizedFetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 10000,
    }
  );
  const isShopsite = useIsShopsite();

  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label="account-popover"
        aria-controls="radix-:R335j9:"
        asChild
      >
        <Button
          variant="ghost"
          shopsiteVariant="ghost"
          className="relative group"
          shopsiteClasses="!p-0"
        >
          <UserIcon
            className={joinClassNames(
              "h-6 w-6 stroke-2",
              isShopsite
                ? "text-white"
                : "text-white group-hover:text-[color:var(--accent-11)] transition-colors ease-in-out duration-300"
            )}
          />
          {!isShopsite && (
            <span className="absolute inset-0 bg-[color:var(--accent-a1)] rounded-lg [&:not(:hover)]:opacity-0 [&:not(:hover)]:animate-fadeOut group-hover:animate-fadeIn" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent open={open}>
        <div
          className={joinClassNames(
            "flex flex-col",
            isShopsite ? "" : "min-w-[19rem]"
          )}
        >
          <div className="flex items-center gap-1 p-1.5">
            <WavingHand className="h-6 w-6" />
            <Heading
              as="h2"
              size="5"
              weight="bold"
              shopsiteClasses="text-xl font-bold"
            >
              {t("hi", { name: account?.firstname ?? "" })}
            </Heading>
          </div>
          <NavLink
            setOpen={setOpen}
            href="/account"
            icon={<UserIcon />}
            label={t("myaccount")}
          />
          <NavLink
            setOpen={setOpen}
            href="/account/my-tickets"
            icon={<TicketIcon />}
            label={t("mytickets")}
          />
          <NavLink
            setOpen={setOpen}
            href="/account/order-history"
            icon={<ClockIcon />}
            label={t("orderhistory")}
          />
          {COUNTRY_CODE !== "au" && (
            <NavLink
              setOpen={setOpen}
              href="/account/e-pass"
              icon={<IdentificationIcon />}
              label={t("epass")}
            />
          )}
          {COUNTRY_CODE !== "au" && (
            <NavLink
              setOpen={setOpen}
              href="/account/subscription-preferences/updates"
              icon={<InboxStackIcon />}
              label={t("subscriptionpreferences")}
            />
          )}
          <NavLink
            setOpen={setOpen}
            onClick={async () => {
              await supabase.auth.signOut();
              await axios.delete("/api/session-params");
              analytics.reset();
              setOpen(false);
              if (pathname === "/account/reset-password") {
                router.replace("/");
              }
              router.refresh();
            }}
            icon={<ArrowRightStartOnRectangleIcon />}
            label={t("signout")}
          />
        </div>
      </PopoverContent>
    </PopoverRoot>
  );
}
